import { useCallback, useState } from 'react'

import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { Box, CircularProgress, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'

export type DragDropFileUploadProps = {
  onFileSelect: (file: File) => Promise<void>
  accept?: string
  maxSize?: number // in MB
  disabled?: boolean
  loading?: boolean
  testId?: string
}

export const DragDropFileUpload = ({
  onFileSelect,
  accept = '.csv,.xlsx,.xls',
  maxSize = 10,
  disabled = false,
  loading = false,
  testId = 'drag-drop-upload'
}: DragDropFileUploadProps) => {
  const [isDragOver, setIsDragOver] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const theme = useTheme()

  const validateFile = useCallback(
    (file: File): boolean => {
      setError(null)

      if (file.size > maxSize * 1024 * 1024) {
        setError(`File size must be less than ${maxSize}MB`)
        return false
      }

      if (accept) {
        const acceptedTypes = accept.split(',').map((type) => type.trim())
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()
        const mimeType = file.type

        const isAccepted = acceptedTypes.some((type) => {
          if (type.startsWith('.')) {
            return fileExtension === type
          }
          return mimeType.includes(type.replace('.', ''))
        })

        if (!isAccepted) {
          setError(`File type not supported. Accepted types: ${accept}`)
          return false
        }
      }

      return true
    },
    [accept, maxSize]
  )

  const handleFile = useCallback(
    async (file: File) => {
      if (validateFile(file)) {
        try {
          await onFileSelect(file)
        } catch (error) {
          console.error('Error processing file:', error)
        }
      }
    },
    [validateFile, onFileSelect]
  )

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (!disabled && !loading) {
        setIsDragOver(true)
      }
    },
    [disabled, loading]
  )

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragOver(false)

      if (disabled || loading) return

      const files = Array.from(e.dataTransfer.files)
      if (files.length > 0) {
        await handleFile(files[0])
      }
    },
    [disabled, loading, handleFile]
  )

  const handleClick = useCallback(() => {
    if (!disabled && !loading) {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = accept
      input.multiple = false
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0]
        if (file) {
          await handleFile(file)
        }
      }
      input.click()
    }
  }, [disabled, loading, accept, handleFile])

  return (
    <Box
      data-testid={testId}
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        border: 2,
        borderStyle: 'dashed',
        borderColor: isDragOver ? 'primary.main' : 'grey.300',
        borderRadius: 2,
        p: 4,
        textAlign: 'center',
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        backgroundColor: isDragOver ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor:
            disabled || loading ? 'transparent' : alpha(theme.palette.primary.main, 0.04),
          borderColor: disabled || loading ? 'grey.300' : 'primary.main'
        },
        opacity: disabled ? 0.6 : 1
      }}
    >
      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            Uploading and processing file...
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          <Box>
            <Typography variant="h6" gutterBottom>
              {isDragOver ? 'Drop file here' : 'Drag & drop file here'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              or click to browse
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Accepted formats: {accept} (max {maxSize}MB)
            </Typography>
          </Box>
        </Box>
      )}

      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}
    </Box>
  )
}
